import React from 'react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { downloadBlob } from '@/lib/report-export';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';

/**
 * CSV import for Products / Customers: choose file → preview → decide → apply.
 * The server (services/record-import.ts) owns matching and validation; this
 * shows its preview, collects decisions for ambiguous customer rows, and
 * applies. Nothing is written until Apply.
 */

type Kind = 'product' | 'customer';
type Action = 'update' | 'create' | 'unchanged' | 'decide' | 'error';
interface PreviewRow {
  row: number; action: Action; matchedBy: string | null; id: string | null; label: string;
  changes: Array<{ field: string; label: string; from: string; to: string }>;
  errors: string[]; warnings: string[]; candidates?: Array<{ id: string; label: string }>;
}
interface Preview { rows: PreviewRow[]; counts: Record<Action, number> }
interface ApplyResult { row: number; ok: boolean; action: 'update' | 'create' | 'skipped'; error?: string }

const KINDS = {
  product: { base: '/catalogue-items', nouns: 'products', match: 'AFM ID, then Item Code' },
  customer: { base: '/master-data/customers', nouns: 'customers', match: 'AFM ID, then JDE Code, then exact Company name' },
};

const ACTION_STYLE: Record<Action, { label: string; cls: string }> = {
  update: { label: 'Update', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300' },
  create: { label: 'New', cls: 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300' },
  decide: { label: 'Needs a decision', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300' },
  unchanged: { label: 'Unchanged', cls: 'bg-muted text-muted-foreground' },
};

/** Download the current records as a CSV in the importer's format. */
export async function exportForImport(kind: Kind) {
  const r = await api<{ filename: string; csv: string }>(`${KINDS[kind].base}/import/export`);
  if (!r.success || !r.data) throw new Error(r.error?.message || 'Export failed');
  downloadBlob(new Blob([r.data.csv], { type: 'text/csv;charset=utf-8' }), r.data.filename);
}

function ImportDialog({ kind, onClose, onComplete }: { kind: Kind; onClose: () => void; onComplete?: () => void }) {
  const cfg = KINDS[kind];
  const [fileName, setFileName] = React.useState('');
  const [csv, setCsv] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<Preview | null>(null);
  const [decisions, setDecisions] = React.useState<Record<string, string>>({});
  const [filter, setFilter] = React.useState<'todo' | Action>('todo');
  const [busy, setBusy] = React.useState<null | 'preview' | 'apply'>(null);
  const [results, setResults] = React.useState<ApplyResult[] | null>(null);

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setFileName(f.name); setPreview(null); setResults(null); setDecisions({});
    const text = await f.text();
    setCsv(text);
    setBusy('preview');
    const r = await api<Preview>(`${cfg.base}/import/preview`, { method: 'POST', body: JSON.stringify({ csv: text }) });
    setBusy(null);
    if (!r.success || !r.data) { toast('Couldn’t read the file', r.error?.message, 'error'); return; }
    setPreview(r.data);
    setFilter(r.data.counts.update + r.data.counts.create + r.data.counts.decide + r.data.counts.error ? 'todo' : 'unchanged');
  };

  const decided = preview?.rows.filter((r) => r.action === 'decide' && decisions[String(r.row)] && decisions[String(r.row)] !== 'skip').length ?? 0;
  const toApply = (preview?.counts.update ?? 0) + (preview?.counts.create ?? 0) + decided;

  const apply = async () => {
    if (!csv) return;
    setBusy('apply');
    const r = await api<ApplyResult[]>(`${cfg.base}/import/apply`, { method: 'POST', body: JSON.stringify({ csv, decisions }) });
    setBusy(null);
    if (!r.success || !r.data) { toast('Import failed', r.error?.message, 'error'); return; }
    setResults(r.data);
    const failed = r.data.filter((x) => !x.ok).length;
    toast(failed ? 'Import finished with errors' : 'Import done', `${r.data.filter((x) => x.ok && x.action !== 'skipped').length} saved${failed ? `, ${failed} failed` : ''}`, failed ? 'error' : 'success');
    onComplete?.();
  };

  const rows = (preview?.rows ?? []).filter((r) => (filter === 'todo' ? r.action !== 'unchanged' : r.action === filter));
  const shownRows = rows.slice(0, 300);

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="lg:max-w-5xl">
        <DialogHeader><DialogTitle>Import {cfg.nouns} from CSV</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto">
          {!results && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-1 text-xs">
              <p><strong>Start from “Export for import”</strong> — it includes each record’s AFM ID, so every row matches its record exactly. Rows without an AFM ID are matched by {cfg.match}; anything else becomes a new record.</p>
              <p><strong>Blank cells are left as they are</strong> — only fill in what should change. Y/N columns take Y or N. Several sites are separated with “;”.</p>
              <p>Nothing is saved until you check the preview and click Apply. Saved records are sent to Weightrax as usual.</p>
            </div>
          )}

          {!results && (
            <label className="flex items-center gap-3">
              <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} disabled={!!busy}
                className="text-sm file:mr-3 file:rounded-md file:border file:bg-background file:px-3 file:py-1.5 file:text-sm" />
              {busy === 'preview' && <span className="text-muted-foreground">Checking {fileName}…</span>}
            </label>
          )}

          {preview && !results && (
            <>
              <div className="flex flex-wrap gap-2">
                {([['todo', `To review (${preview.counts.update + preview.counts.create + preview.counts.decide + preview.counts.error})`],
                  ['update', `Update (${preview.counts.update})`], ['create', `New (${preview.counts.create})`],
                  ['decide', `Needs a decision (${preview.counts.decide})`], ['error', `Errors (${preview.counts.error})`],
                  ['unchanged', `Unchanged (${preview.counts.unchanged})`]] as const).map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setFilter(k)}
                    className={cn('rounded-full border px-3 py-1 text-xs', filter === k ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted')}>
                    {label}
                  </button>
                ))}
              </div>
              {preview.counts.error > 0 && <p className="text-xs text-destructive">Rows with errors are skipped. Fix them in the file and choose it again, or apply the rest now.</p>}
              {rows.length === 0 && <div className="text-muted-foreground text-xs py-4 text-center">No rows here.</div>}
              <div className="border rounded-md divide-y">
                {shownRows.map((r) => (
                  <div key={r.row} className="p-2 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-muted-foreground w-14">Row {r.row}</span>
                      <span className={cn('text-xs rounded-full px-2 py-0.5', ACTION_STYLE[r.action].cls)}>{ACTION_STYLE[r.action].label}</span>
                      <span className="font-medium">{r.label}</span>
                      {r.matchedBy && <span className="text-xs text-muted-foreground">matched by {r.matchedBy}</span>}
                    </div>
                    {r.changes.length > 0 && (
                      <ul className="pl-16 text-xs text-muted-foreground">
                        {r.changes.map((c) => <li key={c.field}>{c.label}: “{c.from}” → <span className="text-foreground">“{c.to}”</span></li>)}
                      </ul>
                    )}
                    {r.errors.map((e) => <div key={e} className="pl-16 text-xs text-destructive">{e}</div>)}
                    {r.warnings.map((w) => <div key={w} className="pl-16 text-xs text-amber-700 dark:text-amber-400">{w}</div>)}
                    {r.action === 'decide' && (
                      <div className="pl-16">
                        <Select value={decisions[String(r.row)] ?? ''} onValueChange={(v) => setDecisions((d) => ({ ...d, [String(r.row)]: v }))}>
                          <SelectTrigger className="h-8 text-xs w-full md:w-[28rem]"><SelectValue placeholder="Choose what to do with this row…" /></SelectTrigger>
                          <SelectContent>
                            {r.candidates?.map((c) => <SelectItem key={c.id} value={c.id}>Update: {c.label}</SelectItem>)}
                            <SelectItem value="new">Create a new customer</SelectItem>
                            <SelectItem value="skip">Skip this row</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {rows.length > shownRows.length && <p className="text-xs text-muted-foreground">Showing the first {shownRows.length} of {rows.length}.</p>}
            </>
          )}

          {results && (
            <div className="space-y-2">
              <p>
                <strong>{results.filter((x) => x.ok && x.action === 'update').length}</strong> updated,{' '}
                <strong>{results.filter((x) => x.ok && x.action === 'create').length}</strong> created,{' '}
                <strong>{results.filter((x) => x.action === 'skipped' && x.ok).length}</strong> skipped,{' '}
                <strong className={results.some((x) => !x.ok) ? 'text-destructive' : undefined}>{results.filter((x) => !x.ok).length}</strong> failed.
              </p>
              <p className="text-xs text-muted-foreground">Saved records are being sent to Weightrax — check the Weightrax column, and the Needs review tab for any that already existed there.</p>
              {results.filter((x) => !x.ok).map((x) => <div key={x.row} className="text-xs text-destructive">Row {x.row}: {x.error}</div>)}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {results ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={!!busy}>Cancel</Button>
              <Button onClick={apply} disabled={!preview || !toApply || !!busy}>
                {busy === 'apply' ? 'Applying…' : toApply ? `Apply ${toApply} ${toApply === 1 ? 'row' : 'rows'}` : 'Nothing to apply'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type DialogProps = { onClose: () => void; onComplete?: () => void };
export const ProductImportDialog = (p: DialogProps) => <ImportDialog kind="product" {...p} />;
export const CustomerImportDialog = (p: DialogProps) => <ImportDialog kind="customer" {...p} />;
